import {
  useCallback,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";

interface UseMutationOptions<TData, TVariables> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  onSuccess?: (data: TData, variables: TVariables) => void | Promise<void>;
  onError?: (error: Error, variables: TVariables) => void;
}

export interface MutateCallOptions<TData> {
  onSuccess?: (data: TData) => void;
  onError?: (error: Error) => void;
  onSettled?: () => void;
}

export interface UseMutationResult<
  TData = unknown,
  _TError = Error,
  TVariables = void,
> {
  mutate: (variables: TVariables, opts?: MutateCallOptions<TData>) => void;
  mutateAsync: (variables: TVariables) => Promise<TData>;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
  data: TData | undefined;
  reset: () => void;
}

type MutationState<TData> = {
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
  data: TData | undefined;
};

const mutationIdle = <TData>(): MutationState<TData> => ({
  isPending: false,
  isSuccess: false,
  isError: false,
  error: null,
  data: undefined,
});

export function useMutation<
  TData = unknown,
  _TError = Error,
  TVariables = void,
>(
  opts: UseMutationOptions<TData, TVariables>,
): UseMutationResult<TData, _TError, TVariables> {
  const [state, setState] = useState<MutationState<TData>>(mutationIdle);
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const mutateAsync = useMutationAsync(optsRef, setState);
  const mutate = useMutationCallback(mutateAsync);
  const reset = useCallback(() => setState(mutationIdle), []);
  return _mutationResult(state, mutate, mutateAsync, reset);
}

function useMutationAsync<TData, TVariables>(
  optsRef: MutableRefObject<UseMutationOptions<TData, TVariables>>,
  setState: Dispatch<SetStateAction<MutationState<TData>>>,
): (variables: TVariables) => Promise<TData> {
  return useCallback(
    async (variables: TVariables): Promise<TData> => {
      setState({ ...mutationIdle(), isPending: true });
      try {
        const data = await optsRef.current.mutationFn(variables);
        setState(_successState(data));
        await optsRef.current.onSuccess?.(data, variables);
        return data;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setState(_errorState(error));
        optsRef.current.onError?.(error, variables);
        throw err;
      }
    },
    [optsRef, setState],
  );
}

function useMutationCallback<TData, TVariables>(
  mutateAsync: (variables: TVariables) => Promise<TData>,
): (variables: TVariables, callOpts?: MutateCallOptions<TData>) => void {
  return useCallback(
    (variables: TVariables, callOpts?: MutateCallOptions<TData>) => {
      void mutateAsync(variables)
        .then((data) => callOpts?.onSuccess?.(data))
        .catch((err: unknown) => {
          const error = err instanceof Error ? err : new Error(String(err));
          callOpts?.onError?.(error);
        })
        .finally(() => callOpts?.onSettled?.());
    },
    [mutateAsync],
  );
}

function _mutationResult<TData, TError, TVariables>(
  state: MutationState<TData>,
  mutate: (variables: TVariables, opts?: MutateCallOptions<TData>) => void,
  mutateAsync: (variables: TVariables) => Promise<TData>,
  reset: () => void,
): UseMutationResult<TData, TError, TVariables> {
  return {
    mutate,
    mutateAsync,
    isPending: state.isPending,
    isSuccess: state.isSuccess,
    isError: state.isError,
    error: state.error,
    data: state.data,
    reset,
  };
}

function _successState<TData>(data: TData): MutationState<TData> {
  return {
    ...mutationIdle(),
    isSuccess: true,
    data,
  };
}

function _errorState<TData>(error: Error): MutationState<TData> {
  return {
    ...mutationIdle(),
    isError: true,
    error,
  };
}
